import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaTasks,
  FaProjectDiagram,
  FaChartLine,
  FaClock,
  FaUsers,
  FaFileAlt,
  FaShieldAlt,
  FaCloud,
  FaMobile,
  FaRobot,
  FaCheckCircle,
  FaArrowRight
} from 'react-icons/fa';
import PublicNav from '../../components/PublicNav/PublicNav';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import './Services.css';

const Services = () => {
  const mainServices = [
    {
      icon: <FaProjectDiagram />,
      title: 'Project Management',
      description: 'Comprehensive project planning, tracking, and execution tools',
      features: [
        'Gantt charts and timelines',
        'Resource allocation',
        'Milestone tracking',
        'Budget management'
      ]
    },
    {
      icon: <FaTasks />,
      title: 'Task Management',
      description: 'Organize and prioritize tasks with powerful workflow tools',
      features: [
        'Kanban boards',
        'Task dependencies',
        'Custom workflows',
        'Priority management'
      ]
    },
    {
      icon: <FaUsers />,
      title: 'Team Collaboration',
      description: 'Real-time collaboration features for distributed teams',
      features: [
        'Team chat',
        'File sharing',
        'Comments & mentions',
        'Activity feeds'
      ]
    },
    {
      icon: <FaChartLine />,
      title: 'Analytics & Reporting',
      description: 'Powerful insights and customizable reports',
      features: [
        'Custom dashboards',
        'Performance metrics',
        'Export capabilities',
        'Automated reports'
      ]
    },
    {
      icon: <FaClock />,
      title: 'Time Tracking',
      description: 'Accurate time tracking and timesheet management',
      features: [
        'Manual & automatic tracking',
        'Billable hours',
        'Time reports',
        'Integration with payroll'
      ]
    },
    {
      icon: <FaFileAlt />,
      title: 'Document Management',
      description: 'Centralized document storage and version control',
      features: [
        'Version history',
        'Access controls',
        'Search functionality',
        'Cloud storage'
      ]
    }
  ];

  const additionalFeatures = [
    {
      icon: <FaShieldAlt />,
      title: 'Enterprise Security',
      description: 'Bank-level encryption and security protocols'
    },
    {
      icon: <FaCloud />,
      title: 'Cloud-Based',
      description: 'Access your projects from anywhere, anytime'
    },
    {
      icon: <FaMobile />,
      title: 'Mobile Apps',
      description: 'Native iOS and Android applications'
    },
    {
      icon: <FaRobot />,
      title: 'AI-Powered',
      description: 'Smart automation and predictive analytics'
    }
  ];

  return (
    <div className="services-page">
      <PublicNav />
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="services-hero-content"
          >
            <h1 className="services-title">Our Services</h1>
            <p className="services-subtitle">
              Everything you need to manage projects, collaborate with teams, 
              and deliver results efficiently.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="main-services-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Core Services</h2>
            <p className="section-subtitle">
              Powerful features designed to streamline your workflow
            </p>
          </motion.div>

          <div className="services-grid">
            {mainServices.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>
                      <FaCheckCircle className="check-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features Section */}
      <section className="additional-features-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Additional Features</h2>
            <p className="section-subtitle">
              More reasons to choose our platform
            </p>
          </motion.div>

          <div className="features-grid">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="integration-section">
        <div className="container">
          <motion.div
            className="integration-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="integration-title">Seamless Integrations</h2>
            <p className="integration-description">
              Connect with your favorite tools and services. We integrate with 
              popular platforms to streamline your workflow.
            </p>
            <div className="integration-logos">
              <div className="logo-placeholder">Slack</div>
              <div className="logo-placeholder">Google Drive</div>
              <div className="logo-placeholder">Jira</div>
              <div className="logo-placeholder">GitHub</div>
              <div className="logo-placeholder">Zoom</div>
              <div className="logo-placeholder">Dropbox</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-subtitle">
              Choose the plan that's right for you and start managing projects like a pro
            </p>
            <div className="cta-buttons">
              <Link to="/pricing" className="btn btn-primary btn-lg">
                View Pricing <FaArrowRight />
              </Link>
              <Link to="/contact" className="btn btn-outline-light btn-lg">
                Contact Sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
};

export default Services;


